import React, { useState } from "react";
import { InstallationData } from "@/installation/installation";
import { useUpdateInstallation } from "@/installation/api";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";

const PHASE_TYPES = [
  { value: "single-phase", label: "Single Phase" },
  { value: "three-phase-star", label: "Three Phase (Star)" },
  { value: "three-phase-delta", label: "Three Phase (Delta)" },
  { value: "multi-loop-primary-a", label: "Multi-loop Primary A" },
  { value: "multi-loop-primary-b", label: "Multi-loop Primary B" },
  { value: "multi-loop-primary-c", label: "Multi-loop Primary C" },
] as const;

type PhaseTypeConfigurationTableProps = {
  assets: InstallationData[];
};

export const PhaseTypeConfigurationTable = ({
  assets,
}: PhaseTypeConfigurationTableProps) => {
  const updateInstallation = useUpdateInstallation();
  const [selectedPhaseTypes, setSelectedPhaseTypes] = useState<
    Record<number, string>
  >({});
  const [updatingIds, setUpdatingIds] = useState<Set<number>>(new Set());

  const handlePhaseTypeChange = (installationId: number, value: string) => {
    setSelectedPhaseTypes((prev) => ({
      ...prev,
      [installationId]: value,
    }));
  };

  const handleUpdate = async (asset: InstallationData) => {
    const phaseType = selectedPhaseTypes[asset.id];
    if (!phaseType) return;

    setUpdatingIds((prev) => new Set(prev).add(asset.id));

    try {
      await updateInstallation.mutateAsync({
        installation_id: asset.id,
        location_id: asset.location.id,
        sublocation_id: asset.sublocation_id,
        phase_system_type: phaseType,
      });
    } finally {
      setUpdatingIds((prev) => {
        const next = new Set(prev);
        next.delete(asset.id);
        return next;
      });
    }
  };

  // Single asset - show simplified layout
  if (assets.length === 1) {
    const asset = assets[0];
    const isUpdating = updatingIds.has(asset.id);
    const selectedPhaseType = selectedPhaseTypes[asset.id];

    return (
      <div className="flex gap-2 items-center">
          <Select
            value={selectedPhaseType}
            onValueChange={(value) => handlePhaseTypeChange(asset.id, value)}
            disabled={isUpdating}
          >
            <SelectTrigger className="w-64">
              <SelectValue placeholder="Select phase type" />
            </SelectTrigger>
            <SelectContent>
              {PHASE_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>
                  {type.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            onClick={() => handleUpdate(asset)}
            disabled={!selectedPhaseType || isUpdating}
          >
            {isUpdating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating...
              </>
            ) : (
              "Update"
            )}
          </Button>
        </div>
    );
  }

  // Multiple assets - show table layout
  return (
    <div className="flex flex-col gap-2">
      <p className="text-center text-sm text-muted-foreground">
        Configure the phase system type for each asset
      </p>
      <div className="max-h-96 overflow-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Installation Name</TableHead>
              <TableHead>Phase Type</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {assets.map((asset) => {
              const isUpdating = updatingIds.has(asset.id);
              const selectedPhaseType = selectedPhaseTypes[asset.id];

              return (
                <TableRow key={asset.id}>
                  <TableCell className="font-medium">{asset.name}</TableCell>
                  <TableCell>
                    <Select
                      value={selectedPhaseType}
                      onValueChange={(value) =>
                        handlePhaseTypeChange(asset.id, value)
                      }
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select phase type" />
                      </SelectTrigger>
                      <SelectContent>
                        {PHASE_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Button
                      onClick={() => handleUpdate(asset)}
                      disabled={!selectedPhaseType || isUpdating}
                      size="sm"
                    >
                      {isUpdating ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        "Update"
                      )}
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
